import { useState, useEffect, useRef } from "react";
import { motion, useInView } from "framer-motion";

// Animated Counter
export const Counter = ({ end, duration = 2, suffix = "" }) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });

  useEffect(() => {
    if (inView) {
      let start = 0;
      const increment = end / (duration * 60);
      const timer = setInterval(() => {
        start += increment;
        if (start >= end) {
          setCount(end);
          clearInterval(timer);
        } else {
          setCount(Math.floor(start));
        }
      }, 1000 / 60);
      return () => clearInterval(timer);
    }
  }, [inView, end, duration]);

  return <span ref={ref}>{count}{suffix}</span>;
};

// Section Animation Wrapper
export const AnimatedSection = ({ children, className = "", delay = 0 }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
      transition={{ duration: 0.7, delay, ease: "easeOut" }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

// DNA Background Animation
export const DNABackground = () => {
  return (
    <div className="dna-container">
      {[...Array(8)].map((_, i) => (
        <div key={`strand-${i}`} className="dna-strand" style={{ left: `${10 + i * 12}%`, animationDelay: `${i * 0.5}s` }} />
      ))}
      {[...Array(20)].map((_, i) => (
        <div key={`particle-${i}`} className="particle" style={{
          left: `${Math.random() * 100}%`, top: `${Math.random() * 100}%`,
          animationDelay: `${Math.random() * 5}s`, animationDuration: `${4 + Math.random() * 4}s`,
        }} />
      ))}
    </div>
  );
};

// Feature Card
export const FeatureCard = ({ icon: Icon, title, description, delay }) => (
  <AnimatedSection delay={delay}>
    <div className="glass rounded-2xl p-8 h-full card-hover group">
      <div className="w-14 h-14 rounded-xl bg-[#00AEEF]/10 flex items-center justify-center mb-6 group-hover:bg-[#00AEEF]/20 transition-colors">
        <Icon className="w-7 h-7 text-[#00AEEF]" />
      </div>
      <h3 className="text-xl font-semibold text-white mb-3">{title}</h3>
      <p className="text-slate-400 text-sm leading-relaxed">{description}</p>
    </div>
  </AnimatedSection>
);

// Timeline Step
export const TimelineStep = ({ step, title, description, index, isLast }) => (
  <AnimatedSection delay={index * 0.1} className="relative">
    <div className="flex items-start gap-6">
      <div className="relative z-10">
        <div className="w-12 h-12 rounded-full bg-[#00AEEF] flex items-center justify-center shadow-[0_0_20px_rgba(0,174,239,0.4)]">
          {step}
        </div>
        {!isLast && (
          <div className="absolute left-1/2 top-12 w-0.5 h-16 bg-gradient-to-b from-[#00AEEF]/50 to-transparent -translate-x-1/2" />
        )}
      </div>
      <div className="flex-1 pb-12">
        <h4 className="text-lg font-semibold text-white mb-2">{title}</h4>
        <p className="text-slate-400 text-sm">{description}</p>
      </div>
    </div>
  </AnimatedSection>
);

// Helper to get background position for cropped vial images
export const getBackgroundPosition = (position) => {
  switch (position) {
    case "left": return "17% center";
    case "center": return "50% center";
    case "right": return "83% center";
    default: return "center center";
  }
};

// Brand image constants
export const BRAND_IMAGES = {
  LOGO: "/api/uploads/brand/logo.png",
  VIAL: "/api/uploads/media/hexxium-vial-transparent.png",
  BRANDED_VIALS: "/api/uploads/brand/branded-vials.jpeg",
  PRODUCTION_LINE: "/api/uploads/brand/production-line.jpeg",
  DNA_HELIX: "/api/uploads/brand/dna-helix.jpeg",
  LAB_EQUIPMENT: "/api/uploads/brand/lab-equipment.jpeg",
  HERO_DNA: "/api/uploads/brand/hero-dna.png",
};
